import { ArrowLeftOutlined } from '@ant-design/icons';
import {
  useGetArchivedSubmissions,
  type ArchivedSubmissionDto,
  type GetArchivedSubmissionsParams,
} from '@framework/api-client';
import { TitleSubmissionRow } from '@framework/common-web';
import { t } from '@framework/i18n';
import {
  AdvancedSearch,
  ColumnSortDirections,
  FilterButton,
  FrontFilterPanel,
  FrontMessageFilteredList,
  getPagination,
  getPaginationUrlColumns,
  handlePaginationChange,
  ResponsiveCard,
  TableData,
  useBreadcrumbItem,
  useHistory,
  usePaginationUrlQueryState,
  type BrandedPath,
} from '@framework/ui';
import { Button, Divider, Space } from 'antd';
import { isEqual, omit } from 'lodash';
import { useMemo, useState } from 'react';
import { type RouteComponentProps } from 'react-router';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import { FilterDrawerButton } from '../../../../ui/drawer/FilterDrawerButton';
import { PageLayout } from '../../../../ui/layout/PageLayout';
import { NetworkAlertPage } from '../../../errors/network-alert/NetworkAlertPage';
import { LoadingPage } from '../../../loading/LoadingPage';
import { archivedSubmissionColumns } from './archivedSubmissionColumns';
import { archivedSubmissionSearchableFields } from './archivedSubmissionSearchableFields';
import { ArchivedSubmissionsQuerySchema } from './ArchivedSubmissionsQuerySchema';

const PAGE_TITLE_ID = 'archived-submissions-list-title-focus-id';
const returnFocusToPageTitle = () => {
  const titleElement = document.getElementById(PAGE_TITLE_ID);
  titleElement?.focus();
};

export interface ArchiveListPageProps extends RouteComponentProps {}

/**
 * Elenco delle richieste archiviate.
 */
export function ArchiveListPage(props: ArchiveListPageProps) {
  const history = useHistory();
  // Pannello Filtro (Desktop)
  const [openSearchPanel, setOpenSearchPanel] = useState(false);
  // Filtro Mobile (Drawer)
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false);
  const isMobile = useBreakpoint(down('md'));

  useBreadcrumbItem({
    path: '/richieste',
    title: 'Richieste',
  });

  useBreadcrumbItem({
    path: props.location.pathname as BrandedPath,
    title: 'Archiviate',
  });

  const defaultQuery = useMemo(
    () =>
      getPagination({
        page: 0,
        pageSize: 10,
        sort: 'sentAt',
        direction: ColumnSortDirections.descend,
      }),
    [],
  );

  const [query, setQuery] =
    usePaginationUrlQueryState<GetArchivedSubmissionsParams>({
      history,
      input: defaultQuery as GetArchivedSubmissionsParams,
    });

  const { data, error, isLoading, isFetching } = useGetArchivedSubmissions(
    query,
    {
      query: { keepPreviousData: true },
      request: {},
    },
  );

  const submissions = data?.items ?? [];
  const meta = data?.meta;

  const columns = getPaginationUrlColumns<
    ArchivedSubmissionDto,
    GetArchivedSubmissionsParams
  >([...archivedSubmissionColumns], query, {
    dataInvio: ['dataInvioDal', 'dataInvioAl'],
  });

  const onPaginationChange = handlePaginationChange<
    ArchivedSubmissionDto,
    GetArchivedSubmissionsParams
  >({
    columns,
    defaultQuery: defaultQuery as GetArchivedSubmissionsParams,
    query,
    setQuery,
    // Non ci sono filtri in tabella, sono gestiti tutti dall'avdvanced search
    getFilters: filters => ({}),
  });

  const isFiltered = useMemo(() => {
    if (isFetching) return false;
    return !isEqual(
      omit(query, ['pagina', 'ordinamenti', 'limite']),
      omit(defaultQuery, ['pagina', 'ordinamenti', 'limite']),
    );
  }, [isFetching, query, defaultQuery]);

  if (isLoading ?? isFetching) return <LoadingPage />;

  if (error) {
    return (
      <NetworkAlertPage
        message={t(
          'archived-submissions.impossibile-caricare-elenco-richieste',
          "Impossibile caricare l'elenco delle Istanze archiviate",
        )}
        error={error}
      />
    );
  }

  const filterform = (
    <AdvancedSearch
      id="archived-submission-list"
      schema={ArchivedSubmissionsQuerySchema}
      fields={archivedSubmissionSearchableFields}
      query={query}
      setQuery={setQuery}
      defaultQuery={defaultQuery as GetArchivedSubmissionsParams}
      pageKey="pagina"
      defaultFieldName="oggetto"
    />
  );

  return (
    <PageLayout
      extraButtons={
        isMobile && (
          <FilterDrawerButton
            open={openFilterDrawer}
            onOpen={() => setOpenFilterDrawer(true)}
            onClose={() => setOpenFilterDrawer(false)}
          >
            {filterform}
          </FilterDrawerButton>
        )
      }
    >
      <ResponsiveCard
        noPadding
        title={
          <TitleSubmissionRow
            id={PAGE_TITLE_ID}
            right={
              <Space size="small">
                <Button
                  onClick={() => history.push('/richieste')}
                  icon={<ArrowLeftOutlined />}
                >
                  {t('archived-submissions.back-to-requests', 'Le mie istanze')}
                </Button>
                {!isMobile && (
                  <FilterButton
                    id="filter-button-submission-list"
                    opened={openSearchPanel}
                    onClick={() => setOpenSearchPanel(!openSearchPanel)}
                  >
                    {t('submission-list.search', 'Ricerca')}
                  </FilterButton>
                )}
              </Space>
            }
          >
            {t(
              'archived-submissions.list-archived-title',
              'Elenco Richieste Archiviate',
            )}
          </TitleSubmissionRow>
        }
      >
        <Divider style={{ margin: 0 }} />

        <FrontFilterPanel
          id="filter-panel-submission-list"
          buttonId="filter-button-submission-list"
          open={!isMobile && openSearchPanel}
        >
          {filterform}
        </FrontFilterPanel>

        <FrontMessageFilteredList
          visible={isFiltered}
          items={meta?.total ?? null}
          message={t(
            'archived-submission.lists-istanze-trovate-in-base-ai-filtri',
            'istanze archiviate trovate in base ai filtri di ricerca.',
          )}
          action={
            <Button
              size="small"
              onClick={() => {
                setQuery(defaultQuery as GetArchivedSubmissionsParams);
                returnFocusToPageTitle();
              }}
              children={t('submission-list.rimuovi-filtri', 'Rimuovi filtri')}
            />
          }
        />

        <TableData
          showSorterTooltip={false}
          size="small"
          dataSource={submissions}
          loading={isLoading || isFetching}
          pagination={{
            current: Number(query.pagina ?? 0) + 1,
            pageSize: query.limite != null ? Number(query.limite) : 10,
            total: meta?.total ?? 0,
            pageSizeOptions: [5, 10, 20, 50],
          }}
          onChange={onPaginationChange}
          columns={columns}
        />
      </ResponsiveCard>
    </PageLayout>
  );
}
